import { useCallback } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import { GET_DASHBOARD_NEW_ENQUIRIES } from '@modules/dashboard/dashboardQueries';
import {
  GET_BROCHURE_SENT_STAGE,
  MARK_ENQUIRY_AS_PROCESSED,
  type SendEmailResponse,
  UPDATE_ENQUIRY_RESPONSE_ACTIVITY,
} from '@modules/enquiries/enquiriesQueries';

type UseEnquiryResponseActionsArgs = {
  enquiryId: string | null | undefined;
  buyerLeadId: string | null | undefined;
  onError: (error: ErrorLike) => void;
  onCompleted?: () => void;
};

export const useEnquiryResponseActions = ({
  enquiryId,
  buyerLeadId,
  onError,
  onCompleted,
}: UseEnquiryResponseActionsArgs) => {
  const [getBrochureSentStage] = useLazyQuery(GET_BROCHURE_SENT_STAGE);

  const [markEnquiryAsProcessed, { loading: markingProcessed }] = useMutation(
    MARK_ENQUIRY_AS_PROCESSED,
    { onError, onCompleted, refetchQueries: [GET_DASHBOARD_NEW_ENQUIRIES] },
  );

  const [updateEnquiryResponseActivity, { loading: updatingResponse }] =
    useMutation(UPDATE_ENQUIRY_RESPONSE_ACTIVITY, {
      onError,
      onCompleted,
      refetchQueries: [GET_DASHBOARD_NEW_ENQUIRIES],
    });

  const markProcessed = useCallback(() => {
    if (enquiryId == null) {
      return;
    }
    markEnquiryAsProcessed({ variables: { id: enquiryId } });
  }, [enquiryId, markEnquiryAsProcessed]);

  const handleEnquiryResponseSent = useCallback(
    (response: unknown) => {
      const responseActivity = (response as SendEmailResponse | null)
        ?.activities?.[0];
      if (
        responseActivity == null ||
        enquiryId == null ||
        buyerLeadId == null
      ) {
        return;
      }

      getBrochureSentStage()
        .then(({ data }) => data?.lead_stages?.[0])
        .catch(() => null)
        .then(brochureSentStage => {
          updateEnquiryResponseActivity({
            variables: {
              enquiryId,
              responseActivityId: responseActivity.id,
              buyerLeadId,
              buyerLeadStage: brochureSentStage?.id,
              updateBuyerLeadStage: brochureSentStage != null,
            },
          });
        });
    },
    [
      enquiryId,
      buyerLeadId,
      getBrochureSentStage,
      updateEnquiryResponseActivity,
    ],
  );

  return {
    markProcessed,
    markingProcessed,
    handleEnquiryResponseSent,
    updatingResponse,
  };
};
